import { gql, useQuery } from '@apollo/client'
import {
  FacebookPage,
  Query,
  TwitterAccount,
} from '@promoboxx/graphql-gateway-types'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

const GET_SOCIAL_ACCOUNTS_FOR_RETAILER_ACCOUNT = gql`
  query getRetailerAccount($id: ID!, $fields: [RetailerAccountFields!]) {
    getRetailerAccount(id: $id, fields: $fields) {
      id
      facebook_pages {
        id
        name
        picture_url
        uid
        instagram_business_account_id
        remote_token_status {
          is_valid
        }
      }
      twitter_accounts {
        id
        uid
        name
        profile_image_url
        authenticated_local
      }
    }
  }
`

export function useRetailerAccountSocialAccounts() {
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const query = useQuery<Query>(GET_SOCIAL_ACCOUNTS_FOR_RETAILER_ACCOUNT, {
    variables: {
      id: retailerAccount?.id,
      fields: [
        'facebook_pages',
        'facebook_pages__remote_token_status',
        'twitter_accounts',
      ],
    },
    skip: retailerAccount?.id == null,
  })

  const facebookPages = useMemo(
    () => query?.data?.getRetailerAccount?.facebook_pages || [],
    [query?.data?.getRetailerAccount?.facebook_pages],
  )

  const twitterAccounts = useMemo(
    () => query?.data?.getRetailerAccount?.twitter_accounts || [],
    [query?.data?.getRetailerAccount?.twitter_accounts],
  )

  const hasInvalidSocialAccount = useMemo(() => {
    const invalidPages =
      facebookPages?.filter(
        (fbp: FacebookPage) => fbp.remote_token_status?.is_valid === false,
      ) || []
    const invalidAccounts =
      twitterAccounts?.filter(
        (ta: TwitterAccount) => ta.authenticated_local === false,
      ) || []
    return !!(
      invalidPages.length ||
      invalidAccounts.length ||
      (facebookPages.length === 0 && twitterAccounts.length === 0)
    )
  }, [facebookPages, twitterAccounts])

  return {
    facebookPages, //If more data is needed, can expand the GQL to include those fields...
    twitterAccounts, //If more data is needed, can expand the GQL to include those fields...
    hasInvalidSocialAccount,
    loading: query.loading,
    refetch: query.refetch,
  }
}
